import { useEffect, useState } from 'react';

/** Current time that re-renders every `cadaMs` (day counters, "hace 5 min"). */
export function useAhora(cadaMs = 60_000): number {
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setAhora(Date.now()), cadaMs);
    return () => clearInterval(id);
  }, [cadaMs]);
  return ahora;
}

const zona = 'America/Santiago';

// Built from parts: browsers disagree on es-CL patterns (24/9 vs 24-09,
// 12h "a. m." vs 24h). The workshop reads 24/09 and 24h everywhere.
const fmtPartes = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
  timeZone: zona,
});
function partes(d: Date) {
  const p = Object.fromEntries(fmtPartes.formatToParts(d).map((x) => [x.type, x.value]));
  return p as Record<'day' | 'month' | 'year' | 'hour' | 'minute' | 'second', string>;
}
const fmtFechaLarga = new Intl.DateTimeFormat('es-CL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  timeZone: zona,
});
const relativo = new Intl.RelativeTimeFormat('es-CL', { numeric: 'auto' });

/** 24/09 */
export function fechaCorta(d: Date) {
  const p = partes(d);
  return `${p.day}/${p.month}`;
}
/** 10:51 */
export function hora(d: Date) {
  const p = partes(d);
  return `${p.hour}:${p.minute}`;
}
/** 24/09 10:51 */
export const fechaHora = (d: Date) => `${fechaCorta(d)} ${hora(d)}`;
/** 24/09/2026 10:51:07 (evidence: full precision) */
export function fechaCompleta(d: Date) {
  const p = partes(d);
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}:${p.second}`;
}
/** 24/09/2026 */
export function fechaConAnio(d: Date) {
  const p = partes(d);
  return `${p.day}/${p.month}/${p.year}`;
}
/** miércoles 24 de septiembre */
export const fechaLarga = (d: Date) => fmtFechaLarga.format(d);

/** "hace 5 minutos", "ayer", "hace 3 días". */
export function hace(d: Date, ahora = Date.now()): string {
  const s = Math.round((d.getTime() - ahora) / 1000);
  const abs = Math.abs(s);
  if (abs < 45) return 'recién';
  if (abs < 3600) return relativo.format(Math.round(s / 60), 'minute');
  if (abs < 86_400) return relativo.format(Math.round(s / 3600), 'hour');
  if (abs < 86_400 * 30) return relativo.format(Math.round(s / 86_400), 'day');
  return fechaCorta(d);
}

/** Start of today in Santiago, in millis. */
export function inicioDelDia(ahora = Date.now()): number {
  const partes = new Intl.DateTimeFormat('en-GB', {
    timeZone: zona,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ahora));
  const n = (t: string) => Number(partes.find((x) => x.type === t)?.value ?? 0);
  return ahora - ((n('hour') * 60 + n('minute')) * 60 + n('second')) * 1000 - (ahora % 1000);
}

const fmtIso = new Intl.DateTimeFormat('en-CA', {
  timeZone: zona,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
/** Calendar date in Santiago as YYYY-MM-DD (compares with `repuesto.llegada`). */
export const diaIso = (d: Date | number = Date.now()) => fmtIso.format(d);

/** "2026-09-29" → 29/09 (parts are dates without time: no timezone shift). */
export function fechaDeIso(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${d}/${m}` : iso;
}

/** Offset of Santiago from UTC at instant `ms`, in millis (e.g. -3 h). */
function desfaseSantiago(ms: number): number {
  const p = partes(new Date(ms));
  const comoUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second),
  );
  return comoUtc - (ms - (ms % 1000));
}

/**
 * "2026-09-29" → the first instant of that day in Santiago. Usually 00:00
 * local; on the day DST starts (September) midnight does not exist and the
 * day starts at 01:00. Candidates come from the offset before and after the
 * guess; the earliest one that falls on the day wins.
 */
export function inicioDiaIso(iso: string): number {
  const [a, m, d] = iso.split('-').map(Number) as [number, number, number];
  const utc = Date.UTC(a, m - 1, d);
  const c1 = utc - desfaseSantiago(utc);
  const c2 = utc - desfaseSantiago(c1);
  const validos = [c1, c2].filter((c) => diaIso(c) === iso);
  return validos.length ? Math.min(...validos) : c2;
}

/** Last millisecond of that Santiago day ("valid until the 30th" includes the 30th). */
export const finDiaIso = (iso: string): number => inicioDiaIso(sumarDiasIso(iso, 1)) - 1;

/** Calendar arithmetic on "YYYY-MM-DD" (no time involved, so no DST surprises). */
export function sumarDiasIso(iso: string, dias: number): string {
  const [a, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(a, m - 1, d + dias)).toISOString().slice(0, 10);
}
